import { useEffect, useState } from 'react';
import { Select, Skeleton } from 'antd';
import {
  HiOutlineDownload,
  HiOutlineCheckCircle,
  HiOutlineInformationCircle,
} from 'react-icons/hi';
import moment from 'moment';
import styled from 'styled-components';

import { Button } from '../../components/Common/Button';
import { PanelLayout } from '../../components/PanelLayout';
import { brlCurrencyFormat } from '../../helpers/brlCurrencyFormat';
import { useAuth } from '../../hooks/useAuth';
import {
  getDividasTributarias,
  DividaTributaria,
  DividaScope,
  SCOPE_LABELS,
  SCOPE_COLORS,
} from '../../operations/dividasTributarias';
import {
  Container,
  PageHeader,
  FilterContainer,
  NoticeContainer,
  NoticeIcon,
  NoticeContent,
  NoticeText,
  DividasList,
  DividaCard,
  DividaCardHeader,
  DividaCardTitle,
  ScopeTag,
  DividaDate,
  DividaValue,
  DividaCardBody,
  DividaInfo,
  NotesText,
  DividaActions,
  EmptyContainer,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  WhatsappContainer,
  WhatsappText,
  WhatsappLink,
  WhatsappImage,
} from './styles';

const SkeletonCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #f0f0f0;
  border-left: 4px solid #e0e0e0;
`;

const SkeletonCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SkeletonCardBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const { Option } = Select;

export const DividasTributarias = () => {
  const { user } = useAuth();
  const [dividas, setDividas] = useState<DividaTributaria[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState<DividaScope | 'all'>('all');

  const fiscalPhone = user && user.tenant_phones && user.tenant_phones['2'];

  useEffect(() => {
    let mounted = true;
    getDividasTributarias()
      .then((data) => {
        if (mounted) {
          setDividas(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching dividas:', error);
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredDividas = scopeFilter === 'all'
    ? dividas
    : dividas.filter((d) => d.scope === scopeFilter);

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const renderEmpty = () => (
    <EmptyContainer>
      <EmptyIcon>
        <HiOutlineCheckCircle />
      </EmptyIcon>
      <EmptyTitle>Nenhuma pendência tributária</EmptyTitle>
      <EmptyDescription>
        Você não possui pendências fiscais no momento.
      </EmptyDescription>
    </EmptyContainer>
  );

  const renderLoading = () => (
    <DividasList>
      {[1, 2, 3].map((i) => (
        <SkeletonCard key={i}>
          <SkeletonCardHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Skeleton.Button active size="small" style={{ width: 70, height: 22 }} />
              <Skeleton.Button active size="small" style={{ width: 80, height: 16 }} />
            </div>
            <Skeleton.Button active size="small" style={{ width: 100, height: 20 }} />
          </SkeletonCardHeader>
          <SkeletonCardBody>
            <Skeleton.Button active size="small" style={{ width: 200, height: 16 }} />
            <Skeleton.Button active size="small" style={{ width: 100, height: 32 }} />
          </SkeletonCardBody>
        </SkeletonCard>
      ))}
    </DividasList>
  );

  const renderDividaCard = (divida: DividaTributaria) => {
    const scopeColor = SCOPE_COLORS[divida.scope];
    const scopeLabel = SCOPE_LABELS[divida.scope];

    return (
      <DividaCard key={divida.id} $scopeColor={scopeColor}>
        <DividaCardHeader>
          <DividaCardTitle>
            <ScopeTag $bgColor={scopeColor}>{scopeLabel}</ScopeTag>
            <DividaDate>
              {moment(divida.notification_date).format('DD/MM/YYYY')}
            </DividaDate>
          </DividaCardTitle>
          {Number(divida.value) > 0 && (
            <DividaValue>{brlCurrencyFormat(Number(divida.value))}</DividaValue>
          )}
        </DividaCardHeader>

        <DividaCardBody>
          <DividaInfo>
            {divida.notes && <NotesText>{divida.notes}</NotesText>}
          </DividaInfo>
          <DividaActions>
            {divida.document_url && (
              <Button
                icon={<HiOutlineDownload />}
                onClick={() => handleDownload(divida.document_url as string)}
              >
                Baixar PDF
              </Button>
            )}
          </DividaActions>
        </DividaCardBody>
      </DividaCard>
    );
  };

  return (
    <PanelLayout>
      <Container>
        <PageHeader>
          <h2>Pendências Tributárias</h2>
          <FilterContainer>
            <Select
              value={scopeFilter}
              onChange={setScopeFilter}
              style={{ width: 150 }}
              placeholder="Filtrar por âmbito"
            >
              <Option value="all">Todos</Option>
              <Option value="federal">Federal</Option>
              <Option value="estadual">Estadual</Option>
              <Option value="municipal">Municipal</Option>
              <Option value="inss">INSS</Option>
              <Option value="fgts">FGTS</Option>
              <Option value="dirpf">DIRPF</Option>
            </Select>
          </FilterContainer>
        </PageHeader>

        <NoticeContainer>
          <NoticeIcon>
            <HiOutlineInformationCircle />
          </NoticeIcon>
          <NoticeContent>
            <NoticeText>
              Realizamos pesquisas recorrentes dos nossos clientes a fim de
              verificar impostos pendentes.
            </NoticeText>
            <NoticeText>
              É importante ressaltar que Receita Federal, Estado, Município e
              INSS não possuem um controle organizado de pendências fiscais.
            </NoticeText>
            <NoticeText>
              Tentamos o quanto antes alertar os clientes quanto ao surgimento de
              pendências, mas somos dependentes das informações provenientes do
              sistema da União.
            </NoticeText>
          </NoticeContent>
        </NoticeContainer>

        {isLoading ? (
          renderLoading()
        ) : filteredDividas.length === 0 ? (
          renderEmpty()
        ) : (
          <DividasList>
            {filteredDividas.map(renderDividaCard)}
          </DividasList>
        )}

        {fiscalPhone && (
          <WhatsappContainer>
            <WhatsappText>Está com dúvidas? Fale com nossa equipe!</WhatsappText>
            <WhatsappLink href={`https://wa.me/55${fiscalPhone}`}>
              <WhatsappImage />
              Falar no WhatsApp
            </WhatsappLink>
          </WhatsappContainer>
        )}
      </Container>
    </PanelLayout>
  );
};

export default DividasTributarias;
